import { createUserAddress } from "../../../State/customer/addressSlice";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { Box, Button, TextField, MenuItem, CircularProgress, Alert } from "@mui/material";
import { useFormik } from "formik";
import React, { useState } from "react";
import * as Yup from "yup";

export const KENYA_COUNTIES = [
    "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo Marakwet", "Embu", "Garissa",
    "Homa Bay", "Isiolo", "Kajiado", "Kakamega", "Kericho", "Kiambu", "Kilifi",
    "Kirinyaga", "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia", "Lamu",
    "Machakos", "Makueni", "Mandera", "Marsabit", "Meru", "Migori", "Mombasa",
    "Murang'a", "Nairobi", "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua",
    "Nyeri", "Samburu", "Siaya", "Taita Taveta", "Tana River", "Tharaka Nithi",
    "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot"
];

const AddressFormSchema = Yup.object().shape({
    name: Yup.string().required("Full Name is required"),
    mobile: Yup.string()
        .required("Mobile number is required")
        .matches(/^(?:254|\+254|0)?(7|1)\d{8}$/, "Enter a valid Kenyan mobile number (e.g., 0712345678)"),
    pinCode: Yup.string()
        .required("Postal code is required (use 00100 or 00000 if unknown)")
        .matches(/^[0-9]{5}$/, "Invalid postal code (e.g., 00100)"),
    address: Yup.string().required("Street Address / Building details are required"),
    city: Yup.string().required("Town / City is required"),
    state: Yup.string().required("County selection is required"),
    locality: Yup.string().required("Locality / Estate is required"),
});

interface AddressFormProps {
    handleClose: () => void;
}

const AddressForm = ({ handleClose }: AddressFormProps) => {
    const dispatch = useAppDispatch();
    const { loading } = useAppSelector((state) => state.address);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const formik = useFormik({
        initialValues: {
            name: '',
            mobile: '',
            pinCode: '00100',
            address: '',
            city: 'Nairobi',
            state: 'Nairobi',
            locality: ''
        },
        validationSchema: AddressFormSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitError(null);
            const jwtToken = localStorage.getItem("jwt") || "";

            if (!jwtToken) {
                setSubmitError("Authentication session expired. Please log in again.");
                return;
            }

            try {
                // .unwrap() ensures catch catches rejectWithValue payloads
                await dispatch(createUserAddress({
                    jwt: jwtToken,
                    address: values
                })).unwrap();

                handleClose(); // Close modal only on successful POST
            } catch (err: any) {
                console.error("Failed to save address:", err);
                setSubmitError(typeof err === "string" ? err : "Failed to create address. Please try again.");
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Box sx={{ maxWidth: 600, mx: "auto" }}>
            <p className='text-xl font-bold text-center pb-5'>Delivery Details (Kenya)</p>

            {submitError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {submitError}
                </Alert>
            )}

            <form onSubmit={formik.handleSubmit}>
                <div className="grid grid-cols-12 gap-6">
                    
                    {/* Full Name */}
                    <div className="col-span-12 sm:col-span-6">
                        <TextField
                            fullWidth
                            name="name"
                            label="Full Name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                        />
                    </div>

                    {/* Mobile Number */}
                    <div className="col-span-12 sm:col-span-6">
                        <TextField
                            fullWidth
                            name="mobile"
                            label="Mobile Number (M-Pesa)"
                            placeholder="0712345678"
                            value={formik.values.mobile}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                            helperText={formik.touched.mobile && formik.errors.mobile}
                        />
                    </div>

                    {/* Locality / Estate */}
                    <div className="col-span-12">
                        <TextField
                            fullWidth
                            name="locality"
                            label="Locality / Estate / Area"
                            placeholder="e.g. Westlands, Kilimani, Pipeline, CBD"
                            value={formik.values.locality}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.locality && Boolean(formik.errors.locality)}
                            helperText={formik.touched.locality && formik.errors.locality}
                        />
                    </div>

                    {/* Physical Address */}
                    <div className="col-span-12">
                        <TextField
                            fullWidth
                            name="address"
                            label="Building / Street Address / House No."
                            placeholder="e.g. Family Trade Center, Ground Floor, Shop G5"
                            value={formik.values.address}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.address && Boolean(formik.errors.address)}
                            helperText={formik.touched.address && formik.errors.address}
                        />
                    </div>

                    {/* Town / City */}
                    <div className="col-span-12 sm:col-span-4">
                        <TextField
                            fullWidth
                            name="city"
                            label="Town / City"
                            placeholder="e.g. Nairobi, Thika, Nakuru"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.city && Boolean(formik.errors.city)}
                            helperText={formik.touched.city && formik.errors.city}
                        />
                    </div>

                    {/* County (State) */}
                    <div className="col-span-12 sm:col-span-4">
                        <TextField
                            fullWidth
                            select
                            name="state"
                            label="County"
                            value={formik.values.state}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.state && Boolean(formik.errors.state)}
                            helperText={formik.touched.state && formik.errors.state}
                        >
                            {KENYA_COUNTIES.map((county) => (
                                <MenuItem key={county} value={county}>
                                    {county}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>

                    {/* Postal Code */}
                    <div className="col-span-12 sm:col-span-4">
                        <TextField
                            fullWidth
                            name="pinCode"
                            label="Postal Code"
                            placeholder="00100"
                            value={formik.values.pinCode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.pinCode && Boolean(formik.errors.pinCode)}
                            helperText={formik.touched.pinCode && formik.errors.pinCode}
                        />
                    </div>

                    {/* Submit Button */}
                    <div className="col-span-12">
                        <Button 
                            fullWidth 
                            type="submit" 
                            variant="contained" 
                            disabled={loading || formik.isSubmitting}
                            sx={{ py: "14px" }}
                        >
                            {loading || formik.isSubmitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                "Save Address"
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </Box>
    );
};

export default AddressForm;